/**
 * <size-impact-slider> — Size bucket selector for art PDPs.
 *
 * Shows all available sizes on a continuous slider with size "buckets" (XS, S, M, L, XL, G).
 *
 * Data sources (checked in order):
 * 1. data-size-options attribute (JSON array, set by sku-picker.js)
 * 2. #SinglePrintPdpConfig script element (photo-print template)
 *
 * Dispatches:
 * - 'size-impact:changed' (on element, bubbles) — SKU picker listens to update state
 * - 'gallery-wall:config-changed' (on document) — frame/room mocks listen to re-render
 */

const SIZE_BUCKETS = [
  { id: 'XS', label: 'Nook', fullName: 'Nook', maxInches: 12, description: 'Small but mighty. Perfect for shelves and gallery nooks.' },   // up to 12"
  { id: 'S',  label: 'Accent',  fullName: 'Accent',       maxInches: 16, description: 'Versatile and balanced. Ideal for entryways and desks.' },   // 13-16"
  { id: 'M',  label: 'Focal Point',  fullName: 'Focal Point',      maxInches: 32, description: 'The Goldilocks size. Large enough to hold its own.' },   // 17-20"
  { id: 'L',  label: 'Statement',  fullName: 'Statement',       maxInches: Infinity, description: 'The anchor. Perfectly balances the width of a sofa.' }   // 21-32"
];

const SP_BUCKET = {
  id: 'SP', label: 'Gallery', fullName: 'Gallery',
  maxInches: Infinity,
  description: 'Two pieces, one vision. Discover curated pairings.',
  isVirtual: true,
  frameSizes: {
    portrait:  { widthInches: 24, heightInches: 32 },
    landscape: { widthInches: 32, heightInches: 24 },
    square:    { widthInches: 24, heightInches: 24 },
  }
};

class SizeImpactSlider extends HTMLElement {
  #allSizes = [];
  #currentIndex = 0;
  #range = null;
  #bucketBoundaries = [];
  #activeBuckets = [];
  #edgeInset = 4;
  #visualPositions = []; // non-linear pct for each size index
  #dispatchTimer = 0;
  #statementPairActive = false;
  #isDragging = false;
  #currentUnit = 'in';
  #unitChangedHandler = null;
  #variantChangedHandler = null;
  #bucketPrices = null; // Per-bucket "from $X" hints (PDP redesign item 3)

  connectedCallback() {
    // 1. Try data-size-options attribute (set by sku-picker.js)
    const attrOptions = this.getAttribute('data-size-options');
    if (attrOptions) {
      try { this.#allSizes = JSON.parse(attrOptions); } catch { /* ignore */ }
    }

    // 2. Fall back to #SinglePrintPdpConfig JSON
    let marketDefaultUnit = null;
    if (!this.#allSizes.length) {
      const el = document.getElementById('SinglePrintPdpConfig');
      if (el) {
        try {
          const config = JSON.parse(el.textContent);
          this.#allSizes = config.options?.size || [];
          if (config.defaultUnit === 'cm' || config.defaultUnit === 'in') {
            marketDefaultUnit = config.defaultUnit;
          }
        } catch { /* ignore */ }
      }
    }

    if (!this.#allSizes.length) return;

    // Filter out sizes only available unframed (not suited for this offering)
    const EXCLUDED_HANDLES = new Set(['42-x-56', '56-x-42']);
    this.#allSizes = this.#allSizes.filter(s => !EXCLUDED_HANDLES.has(s.handle));

    // Sort sizes by max dimension
    this.#allSizes.sort((a, b) => {
      const maxA = Math.max(a.width_inches, a.height_inches);
      const maxB = Math.max(b.width_inches, b.height_inches);
      return maxA - maxB;
    });

    // Calculate which buckets have sizes
    this.#calculateActiveBuckets();

    // PDP redesign item 3: parse per-bucket "from $X" price hints, if present.
    const bucketPricesAttr = this.getAttribute('data-bucket-prices');
    if (bucketPricesAttr) {
      try { this.#bucketPrices = JSON.parse(bucketPricesAttr); } catch { /* ignore */ }
    }

    // Read unit preference: skuPicker instance > localStorage > market default
    const skuPicker = window.skuPickerInstance;
    if (skuPicker?.currentUnit) {
      this.#currentUnit = skuPicker.currentUnit;
    } else {
      const stored = localStorage.getItem('skuPicker_unitPreference');
      if (stored === 'cm' || stored === 'in') {
        this.#currentUnit = stored;
      } else if (marketDefaultUnit) {
        this.#currentUnit = marketDefaultUnit;
      }
    }

    // Listen for unit changes
    this.#unitChangedHandler = (e) => {
      this.#currentUnit = e.detail.unit;
      this.#syncUI();
    };
    document.addEventListener('sku-picker:unit-changed', this.#unitChangedHandler);

    this.#variantChangedHandler = () => this.#syncUI();
    document.addEventListener('sku-picker:variant-changed', this.#variantChangedHandler);
    document.addEventListener('sku-picker:selection-changed', this.#variantChangedHandler);

    // Set default size
    const defaultSize = this.getAttribute('data-default-size') || '';
    this.#currentIndex = Math.max(0, this.#allSizes.findIndex(s => s.handle === defaultSize));

    this.classList.add('no-transition');
    this.#render();
    this.#syncUI();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.classList.remove('no-transition');
    }));
  }

  #getSizeBucket(sizeOption) {
    const maxDim = Math.max(
      parseFloat(sizeOption.width_inches),
      parseFloat(sizeOption.height_inches)
    );
    return SIZE_BUCKETS.find(b => maxDim <= b.maxInches) || SIZE_BUCKETS[SIZE_BUCKETS.length - 1];
  }

  #calculateActiveBuckets() {
    // Determine which buckets have sizes and their slider index ranges
    const bucketMap = new Map();

    this.#allSizes.forEach((size, index) => {
      const bucket = this.#getSizeBucket(size);
      if (!bucketMap.has(bucket.id)) {
        bucketMap.set(bucket.id, { bucket, startIndex: index, endIndex: index });
      } else {
        bucketMap.get(bucket.id).endIndex = index;
      }
    });

    // Build active buckets array in order
    this.#activeBuckets = [];

    SIZE_BUCKETS.forEach(bucket => {
      if (bucketMap.has(bucket.id)) {
        const data = bucketMap.get(bucket.id);
        this.#activeBuckets.push({
          ...bucket,
          startIndex: data.startIndex,
          endIndex: data.endIndex,
        });
      }
    });

    // Append Statement Pair virtual bucket (similar art sourced from tags-discovery-grid)
    if (this.getAttribute('data-enable-gallery-wall') !== 'false') {
      const spIndex = this.#allSizes.length; // one past last real size
      this.#activeBuckets.push({
        ...SP_BUCKET,
        startIndex: spIndex,
        endIndex: spIndex,
      });
    }

    // Equal-gap spacing: every gap between adjacent elements (size notches
    // and bucket dividers) is the same width.  Layout order:
    //   |<->s1<->s2<->|<->s3<->|<->s4<->s5<->s6<->|
    // where | = divider/edge, sN = size notch, <-> = equal step.
    const B = this.#activeBuckets.length;
    const hasSP = this.#activeBuckets.some(b => b.isVirtual);
    const maxIndex = hasSP ? this.#allSizes.length : this.#allSizes.length - 1;

    let totalSizes = 0;
    this.#activeBuckets.forEach(bucket => {
      totalSizes += bucket.endIndex - bucket.startIndex + 1;
    });

    // Total gaps = totalSizes + B  (sizes + B-1 internal dividers + start + end edges = B+1 boundary elements, giving totalSizes + B gaps)
    const totalGaps = totalSizes + B;
    const step = totalGaps > 0 ? 100 / totalGaps : 100;

    this.#bucketBoundaries = [];
    this.#visualPositions = new Array(maxIndex + 1);

    let elementIdx = 0; // start edge at 0%
    this.#activeBuckets.forEach((bucket, i) => {
      const bucketStartPos = elementIdx * step;

      const count = bucket.endIndex - bucket.startIndex + 1;
      for (let j = 0; j < count; j++) {
        elementIdx++;
        this.#visualPositions[bucket.startIndex + j] = elementIdx * step;
      }

      elementIdx++; // divider after sizes (internal divider or end edge)
      const bucketEndPos = elementIdx * step;

      if (i < B - 1) {
        this.#bucketBoundaries.push(bucketEndPos);
      }

      bucket.visualLeftPct = bucketStartPos;
      bucket.visualRightPct = bucketEndPos;
      bucket.visualCenterPct = (bucketStartPos + bucketEndPos) / 2;
    });

    this.#edgeInset = step; // natural edge gap from equal spacing
  }

  #getCurrentBucket() {
    // Virtual SP bucket when index is past all real sizes
    if (this.#currentIndex >= this.#allSizes.length) {
      return this.#activeBuckets.find(b => b.id === 'SP') || null;
    }
    const currentSize = this.#allSizes[this.#currentIndex];
    if (!currentSize) return null;
    return this.#getSizeBucket(currentSize);
  }

  /* ── DOM ── */

  #render() {
    const hasSP = this.#activeBuckets.some(b => b.isVirtual);
    const max = hasSP ? this.#allSizes.length : this.#allSizes.length - 1;
    const currentBucket = this.#getCurrentBucket();

    // Build tick marks HTML - end caps at 0% and 100%, bucket boundaries inset between
    const allTicks = [0, ...this.#bucketBoundaries, 100];
    const ticksHTML = allTicks.map(pct =>
      `<span class="size-bucket-slider__tick" style="left: ${pct}%"></span>`
    ).join('');

    // Build bucket labels HTML - position at visual center of each bucket segment (evenly distributed).
    // Popular badge sits inline next to the bucket label (not stacked below) so the labels row
    // stays a single line height across all stops. Prices then sit immediately below with
    // consistent vertical rhythm regardless of which stop is active.
    const labelsHTML = this.#activeBuckets.map(bucket => {
      const isActive = currentBucket?.id === bucket.id;
      const activeClass = isActive ? ' size-bucket-slider__label--active' : '';
      const popularBadge = bucket.id === 'M' ? '<span class="size-bucket-slider__popular" aria-hidden="true">Popular</span>' : '';
      return `<span class="size-bucket-slider__label${activeClass}" data-bucket="${bucket.id}" style="left: ${bucket.visualCenterPct}%"><span class="size-bucket-slider__label-text">${bucket.label}</span>${popularBadge}</span>`;
    }).join('');

    // PDP redesign item 3: per-stop "from $X" price hint row.
    // Only render when per-bucket pricing was passed in; fallback is a blank row.
    let pricesHTML = '';
    if (this.#bucketPrices) {
      pricesHTML = this.#activeBuckets.map(bucket => {
        const isActive = currentBucket?.id === bucket.id;
        const activeClass = isActive ? ' size-bucket-slider__price--active' : '';
        const priceInfo = this.#bucketPrices[bucket.id];
        // Gallery / Statement Pair is a virtual bucket — no single-unit price hint applies.
        if (!priceInfo || bucket.isVirtual) {
          return `<span class="size-bucket-slider__price${activeClass}" data-bucket="${bucket.id}" style="left: ${bucket.visualCenterPct}%"></span>`;
        }
        // `display` is pre-formatted by sku-picker via the `pdp.size_slider.from_price`
        // translation key (e.g. "from $33"). Falls back to legacy untranslated rendering
        // if a stale bucket payload is passed in.
        const label = priceInfo.display || `from ${priceInfo.fmt}`;
        return `<span class="size-bucket-slider__price${activeClass}" data-bucket="${bucket.id}" style="left: ${bucket.visualCenterPct}%">${label}</span>`;
      }).join('');
    }

    const currentSize = this.#allSizes[this.#currentIndex];
    const headerText = this.#formatHeaderText(currentBucket, currentSize);
    const descriptionText = this.#getBucketDescription(currentBucket);
    const cmActive = this.#currentUnit === 'cm' ? ' active' : '';
    const inActive = this.#currentUnit === 'in' ? ' active' : '';

    this.innerHTML = `
      <div class="size-bucket-slider">
        <div class="size-bucket-slider__header">
          <div class="size-bucket-slider__header-title">Size: <span class="size-bucket-slider__header-value">${headerText}</span></div>
          <span class="size-bucket-slider__unit-toggle"><button type="button" class="size-bucket-slider__unit-btn${inActive}" data-unit="in">in</button><button type="button" class="size-bucket-slider__unit-btn${cmActive}" data-unit="cm">cm</button></span>
        </div>
        <div class="size-bucket-slider__header-description">${descriptionText}</div>
        <div class="size-bucket-slider__track">
          <div class="size-bucket-slider__track-line"></div>
          <div class="size-bucket-slider__ticks">${ticksHTML}</div>
          <input type="range" class="size-bucket-slider__range" min="0" max="${max}" step="1" value="${this.#currentIndex}">
          <span class="size-bucket-slider__thumb"></span>
        </div>
        <div class="size-bucket-slider__labels">${labelsHTML}</div>
        ${pricesHTML ? `<div class="size-bucket-slider__prices">${pricesHTML}</div>` : ''}
      </div>
    `;

    this.#range = this.querySelector('.size-bucket-slider__range');

    // Unit toggle clicks — update preference via sku-picker or localStorage directly
    this.querySelectorAll('.size-bucket-slider__unit-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const newUnit = btn.dataset.unit;
        if (newUnit === this.#currentUnit) return;
        const skuPicker = window.skuPickerInstance;
        if (skuPicker?._setUnitPreference) {
          skuPicker._setUnitPreference(newUnit);
        } else {
          this.#currentUnit = newUnit;
          localStorage.setItem('skuPicker_unitPreference', newUnit);
          document.dispatchEvent(new CustomEvent('sku-picker:unit-changed', {
            detail: { unit: newUnit },
          }));
          this.#syncUI();
        }
      });
    });

    // Keyboard nav (arrow keys) still works via native range input
    this.#range.addEventListener('input', () => {
      this.#currentIndex = parseInt(this.#range.value, 10);
      this.#syncUI();
      this.#scheduleDispatch();
    });

    // Pointer-driven drag uses non-linear visual mapping so the custom
    // thumb aligns with the evenly-spaced bucket boundaries.
    const track = this.querySelector('.size-bucket-slider__track');
    track.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      track.setPointerCapture(e.pointerId);
      // Cache rect once on pointerdown — track doesn't move during drag
      const rect = track.getBoundingClientRect();
      this.#isDragging = true;
      const onPointerMove = (ev) => {
        const pct = ((ev.clientX - rect.left) / rect.width) * 100;
        this.#setIndexFromVisualPct(pct);
      };
      const onPointerUp = () => {
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        this.#isDragging = false;
        if (this.#dispatchTimer) {
          clearTimeout(this.#dispatchTimer);
          this.#dispatchTimer = 0;
        }
        // Final commit — emits size-impact:changed and may trigger variant switch.
        // Safe now that the user has released the pointer.
        this.#dispatch();
      };
      onPointerMove(e); // snap immediately on click
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
    });

    // Switch to room background slide when user starts interacting with slider.
    // Uses a canvas-snapshot cross-fade so the frame mock dissolves smoothly
    // into the room mock instead of a jarring scroll-snap.
    //
    // We copy the frame mock's canvas pixels into an overlay <canvas> (not <img>).
    // Canvas-to-canvas drawImage is fully synchronous — the pixels are composited
    // the instant we append, with no async decode step. This eliminates the race
    // condition where an <img src=dataURL> overlay hadn't painted before the slide
    // switched underneath it.
    const switchToRoomSlide = () => {
      const mediaGallery = document.querySelector('media-gallery');
      const slideshow = mediaGallery?.slideshow;
      if (!slideshow || slideshow.current !== 0) return;

      // Grab the source canvas from the frame mock
      const frameMock = mediaGallery.querySelector(
        'gallery-wall-frame-mock:not([data-corner-crop])'
      );
      const sourceCanvas = frameMock?.canvas;

      if (!sourceCanvas || !sourceCanvas.width) {
        slideshow.select(1);
        return;
      }

      // Position overlay over the current slide
      const slideshowEl = slideshow.refs?.slideshowContainer || slideshow;
      const currentSlide = slideshow.slides?.[0];
      if (!currentSlide) { slideshow.select(1); return; }

      const slideRect = currentSlide.getBoundingClientRect();
      const parentRect = slideshowEl.getBoundingClientRect();

      // Synchronous pixel copy — no decode, no race
      const overlay = document.createElement('canvas');
      overlay.width = sourceCanvas.width;
      overlay.height = sourceCanvas.height;
      overlay.getContext('2d').drawImage(sourceCanvas, 0, 0);

      overlay.className = 'frame-room-transition-overlay';
      overlay.style.cssText = `
        position:absolute; z-index:10; pointer-events:none;
        top:${slideRect.top - parentRect.top}px;
        left:${slideRect.left - parentRect.left}px;
        width:${slideRect.width}px;
        height:${slideRect.height}px;
        opacity:1;
        transition:opacity 350ms ease-out;
        will-change:opacity;
      `;

      // Ensure parent is positioned for absolute child
      const prevPos = slideshowEl.style.position;
      if (!prevPos || prevPos === 'static') slideshowEl.style.position = 'relative';

      slideshowEl.appendChild(overlay);

      // Switch slide immediately — overlay is already painted
      slideshow.select(1, undefined, { animate: false });

      // Double-rAF: let room mock paint underneath, then fade overlay out
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          overlay.style.opacity = '0';

          const cleanup = () => {
            overlay.remove();
            if (prevPos) slideshowEl.style.position = prevPos;
          };
          overlay.addEventListener('transitionend', cleanup, { once: true });
          setTimeout(cleanup, 400); // Safety fallback
        });
      });
    };

    // track.addEventListener('pointerdown', switchToRoomSlide);

    // Flush pending dispatch on keyboard-driven change
    this.#range.addEventListener('change', () => {
      if (this.#dispatchTimer) {
        clearTimeout(this.#dispatchTimer);
        this.#dispatchTimer = 0;
        this.#dispatch();
      }
    });

    // Click on bucket labels to jump to that bucket
    this.querySelectorAll('.size-bucket-slider__label').forEach(label => {
      label.addEventListener('click', () => {
        const bucketId = label.dataset.bucket;
        const bucket = this.#activeBuckets.find(b => b.id === bucketId);
        if (bucket) {
          // Jump to middle of bucket
          this.#currentIndex = Math.floor((bucket.startIndex + bucket.endIndex) / 2);
          this.#range.value = this.#currentIndex;
          this.#syncUI();
          this.#dispatch();
        }
      });
    });

    // Inject stylesheet if not already present
    if (!document.getElementById('size-impact-slider-styles')) {
      const link = document.createElement('link');
      link.id = 'size-impact-slider-styles';
      link.rel = 'stylesheet';
      link.href = this.#assetUrl('size-impact-slider.css');
      document.head.appendChild(link);
    }
  }

  #assetUrl(filename) {
    const existing = document.querySelector('link[href*="/assets/"], script[src*="/assets/"]');
    if (existing) {
      const url = existing.href || existing.src;
      return url.replace(/\/assets\/[^?]+/, `/assets/${filename}`);
    }
    return `/assets/${filename}`;
  }

  /* ── State sync ── */

  #formatHeaderText(bucket, size) {
    if (!bucket || !size) return '';
    if (this.#currentUnit === 'cm') {
      const w = parseFloat(size.width_cms);
      const h = parseFloat(size.height_cms);
      if (!w || !h) return bucket.fullName;
      return `${bucket.fullName} (${w} × ${h}cm)`;
    }
    const w = Math.round(parseFloat(size.width_inches));
    const h = Math.round(parseFloat(size.height_inches));
    return `${bucket.fullName} (${w} × ${h}")`;
  }

  #getBucketDescription(bucket) {
    if (!bucket) return '';
    return bucket.description || '';
  }

  #syncUI() {
    const isSP = this.#currentIndex >= this.#allSizes.length;
    const currentBucket = this.#getCurrentBucket();

    if (!isSP) {
      const currentSize = this.#allSizes[this.#currentIndex];
      if (!currentSize) return;

      const sizeBucket = this.#getSizeBucket(currentSize);

      // Update active states for labels
      this.querySelectorAll('.size-bucket-slider__label').forEach(el => {
        el.classList.toggle('size-bucket-slider__label--active', el.dataset.bucket === sizeBucket.id);
      });

      const currentSizePrice = window.skuPickerInstance?._getCurrentSizePrice?.(currentSize.handle);
      this.querySelectorAll('.size-bucket-slider__price').forEach(el => {
        const isActive = el.dataset.bucket === sizeBucket.id;
        el.classList.toggle('size-bucket-slider__price--active', isActive);
        if (isActive && currentSizePrice) {
          el.textContent = currentSizePrice.formatted;
        } else if (this.#bucketPrices) {
          const info = this.#bucketPrices[el.dataset.bucket];
          if (info) el.textContent = info.display || `from ${info.fmt}`;
        }
      });

      // Update header with current size info
      const headerValueEl = this.querySelector('.size-bucket-slider__header-value');
      if (headerValueEl) {
        headerValueEl.textContent = this.#formatHeaderText(sizeBucket, currentSize);
      }

      // Update description with crossfade
      const descEl = this.querySelector('.size-bucket-slider__header-description');
      if (descEl) {
        const newDesc = this.#getBucketDescription(sizeBucket);
        if (descEl.textContent !== newDesc) {
          descEl.classList.add('is-fading');
          setTimeout(() => {
            descEl.textContent = newDesc;
            descEl.classList.remove('is-fading');
          }, 150);
        }
      }
    } else {
      // Statement Pair mode — show label without dimensions
      this.querySelectorAll('.size-bucket-slider__label').forEach(el => {
        el.classList.toggle('size-bucket-slider__label--active', el.dataset.bucket === 'SP');
      });
      this.querySelectorAll('.size-bucket-slider__price').forEach(el => {
        el.classList.toggle('size-bucket-slider__price--active', el.dataset.bucket === 'SP');
      });

      const headerValueEl = this.querySelector('.size-bucket-slider__header-value');
      if (headerValueEl) {
        headerValueEl.textContent = 'Gallery';
      }

      const descEl = this.querySelector('.size-bucket-slider__header-description');
      if (descEl) {
        const newDesc = SP_BUCKET.description;
        if (descEl.textContent !== newDesc) {
          descEl.classList.add('is-fading');
          setTimeout(() => {
            descEl.textContent = newDesc;
            descEl.classList.remove('is-fading');
          }, 150);
        }
      }
    }

    // Position custom thumb using non-linear visual mapping.
    // Uses transform instead of left to avoid layout thrashing during transitions.
    const thumb = this.querySelector('.size-bucket-slider__thumb');
    if (thumb) {
      const pos = this.#visualPositions[this.#currentIndex] ?? this.#edgeInset;
      const track = this.querySelector('.size-bucket-slider__track');
      const px = track ? (pos / 100) * track.offsetWidth : 0;
      thumb.style.transform = `translate(calc(${px}px - 50%), -50%)`;
    }

    // Sync unit toggle active states
    this.querySelectorAll('.size-bucket-slider__unit-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.unit === this.#currentUnit);
    });
  }

  /** Map a visual track percentage to the nearest size index. */
  #setIndexFromVisualPct(pct) {
    let closest = 0;
    let minDist = Infinity;
    for (let i = 0; i < this.#visualPositions.length; i++) {
      const dist = Math.abs((this.#visualPositions[i] ?? Infinity) - pct);
      if (dist < minDist) { minDist = dist; closest = i; }
    }
    if (closest !== this.#currentIndex) {
      this.#currentIndex = closest;
      this.#range.value = closest;
      this.#syncUI();
      this.#scheduleDispatch();
    }
  }

  /* ── Events ── */

  #scheduleDispatch() {
    clearTimeout(this.#dispatchTimer);
    this.#dispatchTimer = setTimeout(() => {
      // During an active drag, only emit the cheap visual-update event.
      // The expensive size-impact:changed (which can trigger a variant switch
      // and full main morph that rips the slider out from under the pointer)
      // is held until pointerup where it fires exactly once.
      if (this.#isDragging) {
        this.#dispatchVisual();
      } else {
        this.#dispatch();
      }
    }, 100);
  }

  /** Visual-only broadcast — safe to fire mid-drag. */
  #dispatchVisual() {
    if (this.#currentIndex >= this.#allSizes.length) return;
    const currentSize = this.#allSizes[this.#currentIndex];
    if (!currentSize) return;
    const widthInches = parseFloat(currentSize.width_inches);
    const heightInches = parseFloat(currentSize.height_inches);
    document.dispatchEvent(new CustomEvent('gallery-wall:config-changed', {
      bubbles: true,
      detail: {
        size: currentSize.handle,
        wallDimensions: { widthInches, heightInches },
        artworkWidth: widthInches,
      },
    }));
  }

  #dispatch() {
    const isSP = this.#currentIndex >= this.#allSizes.length;

    if (isSP) {
      // Entering Statement Pair mode
      if (!this.#statementPairActive) {
        this.#statementPairActive = true;
        // Determine orientation from the product's aspect ratio
        const lastSize = this.#allSizes[this.#allSizes.length - 1];
        const w = parseFloat(lastSize?.width_inches) || 1;
        const h = parseFloat(lastSize?.height_inches) || 1;
        const orientation = w > h ? 'landscape' : h > w ? 'portrait' : 'square';
        const frameSize = SP_BUCKET.frameSizes[orientation];
        document.dispatchEvent(new CustomEvent('statement-pair:toggled', {
          bubbles: true,
          detail: { active: true, ...frameSize },
        }));
      }
      return;
    }

    // Leaving Statement Pair mode — restore normal events
    if (this.#statementPairActive) {
      this.#statementPairActive = false;
      document.dispatchEvent(new CustomEvent('statement-pair:toggled', {
        bubbles: true,
        detail: { active: false },
      }));
    }

    const currentSize = this.#allSizes[this.#currentIndex];
    if (!currentSize) return;

    // 1. Visual broadcast (frame mocks, room mocks)
    this.#dispatchVisual();

    // 2. Notify SKU picker of size change (bubbles up to container).
    //    This may trigger a variant switch + main section morph, so it's
    //    gated on drag-completion by #scheduleDispatch.
    this.dispatchEvent(new CustomEvent('size-impact:changed', {
      bubbles: true,
      detail: { handle: currentSize.handle, index: this.#currentIndex },
    }));
  }

  disconnectedCallback() {
    clearTimeout(this.#dispatchTimer);
    if (this.#unitChangedHandler) {
      document.removeEventListener('sku-picker:unit-changed', this.#unitChangedHandler);
    }
    if (this.#variantChangedHandler) {
      document.removeEventListener('sku-picker:variant-changed', this.#variantChangedHandler);
      document.removeEventListener('sku-picker:selection-changed', this.#variantChangedHandler);
    }
  }
}

// Guard: if URL param explicitly disables the size slider, don't register
// the custom element. Prevents re-activation via section render morphs.
if (new URLSearchParams(window.location.search).get('pdp_slider') !== '0') {
  if (!customElements.get('size-impact-slider')) {
    customElements.define('size-impact-slider', SizeImpactSlider);
  }
}
